import React, { useMemo, useState } from "react";
import DOMPurify from "isomorphic-dompurify";
import { styled } from "@acko-tech/building-blocks.ui.common";
import { PrimaryButton } from "@acko-tech/building-blocks.ui.button";
import Modal from "@components/modal";
import Badge from "@components/common/badge";
import Hyperlink from "@components/common/hyperlink";
import { convertToLakh, formatToINR } from "@utils/inrFormatter";
import { AddonVariant, TpAddonModel } from "./model";

export type TpPaModalCopy = {
  badgeLabel?: string;
  heading?: string;
  nudgeHeading?: string;
  description?: string;
  coverageList?: string[];
  socialProof?: string;
  primaryCtaLabel?: string;
  keepCtaLabel?: string;
  updateCtaLabel?: string;
  removeCtaLabel?: string;
  nudgePrimaryCtaLabel?: string;
  nudgeSecondaryCtaLabel?: string;
  preferredVariantId?: string;
};

type TpPaCoverModalProps = {
  addon: TpAddonModel;
  copy: TpPaModalCopy;
  isCoverSelected: boolean;
  mode: "selector" | "nudge";
  selectedVariantId?: string;
  onClose: () => void;
  onContinueWithout: () => void;
  onRemove: () => void;
  onSelect: (variant: AddonVariant) => void;
};

const Content = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

const Heading = styled.h2`
  margin: 16px 32px 0 0;
  color: #040222;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  letter-spacing: -0.1px;

  span {
    color: #7c47e1;
  }
`;

const Description = styled.p`
  margin: 16px 0 0;
  color: #36354c;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

const CoverageList = styled.ul`
  margin: 12px 0 0;
  padding-left: 20px;
  color: #36354c;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;

  li + li {
    margin-top: 12px;
  }
`;

const Variants = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 32px;

  @media (max-width: 359px) {
    grid-template-columns: 1fr;
  }
`;

const VariantCard = styled.button`
  position: relative;
  display: flex;
  min-height: 78px;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 18px 12px 12px;
  border: 1px solid ${(props) => (props.isSelected ? "#0fa457" : "#e7e7f0")};
  border-radius: 12px;
  outline: none;
  background: ${(props) => (props.isSelected ? "#f3faf7" : "#ffffff")};
  color: #040222;
  cursor: pointer;
  text-align: left;

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(89, 32, 197, 0.2);
  }
`;

const VariantCoverage = styled.span`
  color: #040222;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;

  strong {
    color: #0b753e;
  }
`;

const VariantPrice = styled.span`
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-top: 4px;
  color: #040222;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
`;

const StrikeThroughPrice = styled.del`
  color: #5b5675;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

const VariantBadge = styled.div`
  position: absolute;
  top: -14px;
  left: 12px;
`;

const SocialProof = styled.div`
  margin: 32px -20px 0;
  padding: 11px 20px;
  background: #efe9fb;
  color: #451999;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  text-align: center;

  b,
  strong {
    font-weight: 600;
  }
`;

const Footer = styled.footer`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin: ${(props) =>
    props.hasSocialProof ? "0 -20px -20px" : "32px -20px -20px"};
  padding: 12px 20px 20px;
  background: #ffffff;
`;

const sanitize = (value: string) => ({
  __html: DOMPurify.sanitize(value)
});

const getFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const getInitialVariantId = (
  addon: TpAddonModel,
  selectedVariantId?: string,
  preferredVariantId?: string
) => {
  const availableIds = new Set(
    addon.variants.map((variant) => variant.variant_id).filter(Boolean)
  );

  if (selectedVariantId && availableIds.has(selectedVariantId)) {
    return selectedVariantId;
  }
  if (preferredVariantId && availableIds.has(preferredVariantId)) {
    return preferredVariantId;
  }
  if (addon.variantId && availableIds.has(addon.variantId)) {
    return addon.variantId;
  }
  return addon.variants[0]?.variant_id || "";
};

const getBadgeType = (type?: string): "PREMIUM" | "DANGER" | "GENERIC" => {
  const normalizedType = type?.trim().toUpperCase();
  if (normalizedType === "PREMIUM" || normalizedType === "DANGER") {
    return normalizedType;
  }
  return "GENERIC";
};

const TpPaCoverModal = ({
  addon,
  copy,
  isCoverSelected,
  mode,
  selectedVariantId,
  onClose,
  onContinueWithout,
  onRemove,
  onSelect
}: TpPaCoverModalProps) => {
  const [activeVariantId, setActiveVariantId] = useState(() =>
    getInitialVariantId(addon, selectedVariantId, copy.preferredVariantId)
  );
  const variants = useMemo(
    () =>
      [...addon.variants].sort(
        (first, second) =>
          Number(second.sum_assured_value || 0) -
          Number(first.sum_assured_value || 0)
      ),
    [addon.variants]
  );
  const activeVariant =
    variants.find((variant) => variant.variant_id === activeVariantId) ||
    variants[0];
  const isNudge = mode === "nudge";
  const showSocialProof = Boolean(
    !isNudge && !isCoverSelected && copy.socialProof?.trim()
  );
  const heading = isNudge ? copy.nudgeHeading : copy.heading;
  const primaryCtaLabel = isNudge
    ? copy.nudgePrimaryCtaLabel || copy.primaryCtaLabel
    : isCoverSelected
      ? activeVariant?.variant_id === selectedVariantId
        ? copy.keepCtaLabel || copy.primaryCtaLabel
        : copy.updateCtaLabel || copy.primaryCtaLabel
      : copy.primaryCtaLabel;

  return (
    <Modal
      showModal
      actions={{}}
      onClose={onClose}
      styles={{ position: "relative" }}
      bodyStyles={{ overflow: "visible" }}
    >
      <Content>
        {copy.badgeLabel ? (
          <Badge
            text={copy.badgeLabel}
            type="DANGER"
            is_visible
            textStyles={{ fontWeight: 600 }}
          />
        ) : null}

        {heading ? (
          <Heading dangerouslySetInnerHTML={sanitize(heading)} />
        ) : null}

        {copy.description ? (
          <Description dangerouslySetInnerHTML={sanitize(copy.description)} />
        ) : null}

        {copy.coverageList?.length ? (
          <CoverageList>
            {copy.coverageList.map((item, index) => (
              <li
                key={`${item}-${index}`}
                dangerouslySetInnerHTML={sanitize(item)}
              />
            ))}
          </CoverageList>
        ) : null}

        <Variants role="radiogroup" aria-label={addon.displayName}>
          {variants.map((variant) => {
            const netPremium = getFiniteNumber(variant.price?.net_premium);
            const strikeThroughPremium =
              getFiniteNumber(variant.price?.net_strike_through_premium) ??
              getFiniteNumber(variant.price?.old_net_premium);
            const coverageAmount = convertToLakh(
              variant.sum_assured_value || 0
            );
            const isSelected = variant.variant_id === activeVariant?.variant_id;

            return (
              <VariantCard
                aria-checked={isSelected}
                isSelected={isSelected}
                key={variant.variant_id}
                onClick={() => setActiveVariantId(variant.variant_id || "")}
                role="radio"
                type="button"
              >
                {variant.badge?.text ? (
                  <VariantBadge>
                    <Badge
                      text={variant.badge.text}
                      type={getBadgeType(variant.badge.type)}
                      is_visible
                    />
                  </VariantBadge>
                ) : null}
                <VariantCoverage>
                  <strong>₹{coverageAmount} lakh</strong> coverage
                </VariantCoverage>
                {netPremium !== null ? (
                  <VariantPrice>
                    {formatToINR(netPremium, true)}
                    {strikeThroughPremium !== null &&
                    strikeThroughPremium > netPremium ? (
                      <StrikeThroughPrice>
                        {formatToINR(strikeThroughPremium, true)}
                      </StrikeThroughPrice>
                    ) : null}
                  </VariantPrice>
                ) : null}
              </VariantCard>
            );
          })}
        </Variants>

        {showSocialProof && copy.socialProof ? (
          <SocialProof dangerouslySetInnerHTML={sanitize(copy.socialProof)} />
        ) : null}

        <Footer hasSocialProof={showSocialProof}>
          <PrimaryButton
            isFullWidth
            disabled={!activeVariant || !primaryCtaLabel}
            onClick={() => {
              if (!activeVariant) return;
              onSelect(activeVariant);
              onClose();
            }}
          >
            {primaryCtaLabel}
          </PrimaryButton>

          {isNudge && copy.nudgeSecondaryCtaLabel ? (
            <Hyperlink
              text={copy.nudgeSecondaryCtaLabel}
              type="PRIMARY"
              styles={{ fontSize: "14px", lineHeight: "20px" }}
              clickHandler={() => {
                onContinueWithout();
                onClose();
              }}
            />
          ) : null}

          {!isNudge && isCoverSelected && copy.removeCtaLabel ? (
            <Hyperlink
              text={copy.removeCtaLabel}
              type="DANGER"
              styles={{ fontSize: "14px", lineHeight: "20px" }}
              clickHandler={() => {
                onRemove();
                onClose();
              }}
            />
          ) : null}
        </Footer>
      </Content>
    </Modal>
  );
};

export default TpPaCoverModal;
